// Ghost Driver web-schermen — visuele re-creatie van de app-routes waardoor
// het script navigeert (deeplinks). Zelfde design-taal als de app (theme.ts).
import React from 'react';
import Icon from '../Icon';
import { C } from '../theme';
import { PROPS, NOTIFS, MARKTPULSE } from './data';
import TabBarApp from './app/TabBar';
import OntdekApp from './app/Ontdek';
import SwipeApp from './app/Swipe';
import BezoekenApp from './app/Bezoeken';
import { OnbIntro, OnbMode, OnbPartner, OnbProfile, OnbType, OnbPrefs, OnbSearching, OnbFirstMatch } from './app/Onboarding';

const NAVY = '#0e2340';
const GOLD = '#c48a3a';

// ── Gedeelde bouwblokken ────────────────────────────────────────
function AppHeader({ mode }) {
  return (
    <div style={{
      background: '#102A43', padding: '46px 16px 12px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
    }}>
      <div style={{ color: '#fff', fontSize: 16, fontWeight: 900, letterSpacing: 1.1 }}>
        IMMO<span style={{ color: '#C48A3A', fontStyle: 'italic', fontWeight: 400 }}>match</span>
        <span style={{ color: '#C48A3A', fontSize: 9, fontWeight: 900, letterSpacing: 1.3, marginLeft: 6 }}>DEMO</span>
      </div>
      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 600 }}>
        {mode === 'couple' ? 'Tom & Emma' : 'Sofie'}
      </div>
    </div>
  );
}

export function TabBar({ active }) {
  return <TabBarApp active={active} />;
}

const screen = { minHeight: '100%', background: C.bgSoft, paddingBottom: 24 };
const body = { padding: '18px 16px 24px', display: 'flex', flexDirection: 'column', gap: 12 };
const card = { background: '#fff', borderRadius: 16, padding: 14, border: `1px solid ${C.line}`, boxShadow: '0 2px 10px rgba(16,42,67,0.05)' };
const eyebrow = { color: C.gold, fontSize: 9.5, fontWeight: 900, letterSpacing: 1.2 };
const h2 = { color: C.navy, fontSize: 20, fontWeight: 900, letterSpacing: -0.3, margin: 0 };
const sub = { color: C.inkMuted, fontSize: 12.5, lineHeight: '17px' };

function Eyebrow({ children }) {
  return (
    <div style={{
      alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 5,
      background: 'rgba(196,138,58,0.14)', border: '1px solid rgba(196,138,58,0.45)',
      padding: '4px 10px', borderRadius: 999,
    }}>
      <span style={eyebrow}>{children}</span>
    </div>
  );
}

function PropertyCard({ p, compact }) {
  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div style={{ position: 'relative' }}>
        <img src={p.img} alt={p.title} style={{ width: '100%', height: compact ? 120 : 150, objectFit: 'cover', display: 'block' }} />
        <div style={{ position: 'absolute', top: 10, left: 10, background: GOLD, padding: '4px 9px', borderRadius: 999 }}>
          <span style={{ color: '#fff', fontSize: 11, fontWeight: 900 }}>{p.match}% match</span>
        </div>
      </div>
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ color: C.navy, fontSize: 14, fontWeight: 900 }}>{p.title}</div>
        <div style={{ color: C.inkMuted, fontSize: 11.5 }}>{p.city} · {p.price} · {p.beds} slpk · EPC {p.epc}</div>
        {!compact && <div style={{ color: C.inkMuted, fontSize: 11, fontStyle: 'italic' }}>{p.why}</div>}
      </div>
    </div>
  );
}

// ── Splash (/demo/splash) — port van splash.tsx ─────────────────
function Splash() {
  return (
    <div data-testid="demo-splash" style={{ minHeight: '100%', background: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div className="anim-pop" style={{ width: 148, height: 148, margin: '0 auto 22px' }}>
          <img src="/logo_official.png" alt="IMMO MATCH" style={{ width: '100%', height: '100%', objectFit: 'contain', animation: 'immoBeat .44s ease .9s 1' }} />
        </div>
        <div className="anim-fade-in" style={{ animationDelay: '.75s' }}>
          <span style={{ color: '#fff', fontSize: 40, fontWeight: 800, letterSpacing: 1.8 }}>IMMO</span>
          <span style={{ color: '#C48A3A', fontSize: 38, fontWeight: 400, fontStyle: 'italic', letterSpacing: 0.6 }}>match</span>
        </div>
        <div className="anim-fade-in" style={{ animationDelay: '1.25s', color: 'rgba(255,255,255,0.75)', fontSize: 14.5, fontWeight: 600, letterSpacing: 0.6, marginTop: 4 }}>
          Jouw persoonlijke Wooncoach
        </div>
      </div>
    </div>
  );
}

// ── Onboarding-schermen ─────────────────────────────────────────
// Fasen 1-8: ingevulde toestand met de exacte Ghost Driver showcase-voorkeuren
// (bron: auto_demo.py → _seed_ghost_driver_preferences).
// ── Woningdetail (/property/:id) ────────────────────────────────
function PropertyDetail({ mode }) {
  const p = mode === 'couple' ? PROPS.demo_p2 : PROPS.demo_p1;
  return (
    <div style={{ ...screen, paddingBottom: 32 }}>
      <div style={{ position: 'relative' }}>
        <img src={p.img} alt={p.title} style={{ width: '100%', height: 230, objectFit: 'cover', display: 'block' }} />
        <div style={{ position: 'absolute', top: 48, left: 14, width: 34, height: 34, borderRadius: 17, background: 'rgba(14,35,64,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="chevron-left" size={17} color="#fff" />
        </div>
        <div style={{ position: 'absolute', top: 48, right: 14, background: GOLD, padding: '5px 11px', borderRadius: 999 }}>
          <span style={{ color: '#fff', fontSize: 12, fontWeight: 900 }}>{p.match}% match</span>
        </div>
      </div>
      <div style={body}>
        <div style={{ color: C.navy, fontSize: 19, fontWeight: 900 }}>{p.title}</div>
        <div style={sub}>{p.city} · {mode === 'solo' ? `${p.price} (nu ${p.priceDropped})` : p.price} · {p.surface} m²</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[[`${p.beds} slaapkamers`, 'home'], [`EPC ${p.epc}`, 'zap'], ...p.tags.map((t) => [t, 'check'])].map(([t, ic], i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', border: `1px solid ${C.line}`, borderRadius: 999, padding: '6px 11px', color: C.navy, fontSize: 11.5, fontWeight: 700 }}>
              <Icon name={ic} size={11} color={C.gold} />{t}
            </span>
          ))}
        </div>
        <div style={{ ...card, background: 'rgba(196,138,58,0.10)', borderColor: 'rgba(196,138,58,0.35)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Icon name="info" size={14} color={C.gold} style={{ marginTop: 1, flexShrink: 0 }} />
          <span style={{ ...sub, color: C.navy }}>{p.why}</span>
        </div>
        <div style={card}>
          <div style={{ color: C.navy, fontSize: 13, fontWeight: 900, marginBottom: 8 }}>Buurt & bereikbaarheid</div>
          {[['Station Gent', mode === 'couple' ? '8 min fietsen' : '4 min fietsen'], ['Supermarkt', '6 min wandelen'], ['School', '9 min wandelen'], ['Groen / park', '5 min wandelen']].map(([l, v], i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderTop: i ? '1px solid #F0EBE3' : 'none' }}>
              <span style={{ color: C.inkMuted, fontSize: 12 }}>{l}</span>
              <span style={{ color: C.navy, fontSize: 12, fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={card}>
          <div style={{ color: C.navy, fontSize: 13, fontWeight: 900, marginBottom: 6 }}>Omschrijving</div>
          <div style={{ ...sub, fontSize: 12, lineHeight: '17px' }}>
            Karaktervolle woning met hoge plafonds, visgraatparket en een zonnige tuin. Rustige zijstraat, toch dicht bij het station en het centrum van Gent.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Favorieten (/maybes) ────────────────────────────────────────
function Maybes({ mode }) {
  const list = mode === 'couple' ? [PROPS.demo_p2, PROPS.demo_p1, PROPS.demo_p4] : [PROPS.demo_p1, PROPS.demo_p4, PROPS.demo_p3];
  return (
    <div style={screen}>
      <AppHeader mode={mode} />
      <div style={body}>
        <h2 style={h2}>{mode === 'couple' ? 'Jullie shortlist' : 'Favorieten'}</h2>
        <div style={sub}>{mode === 'couple' ? '3 gezamenlijke woningen om te vergelijken' : 'Je opgeslagen woningen om te vergelijken'}</div>
        {list.map((p, i) => (
          <div key={i} style={{ ...card, display: 'flex', gap: 12, alignItems: 'center', padding: 10 }}>
            <img src={p.img} alt={p.title} style={{ width: 74, height: 74, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ color: C.navy, fontSize: 13, fontWeight: 800 }}>{p.title}</div>
              <div style={{ ...sub, fontSize: 11, marginTop: 2 }}>{p.city} · {p.price} · {p.match}% match</div>
            </div>
            <Icon name="heart" size={15} color="#e0559c" />
          </div>
        ))}
        <div style={{ ...card, display: 'flex', gap: 10, alignItems: 'center', background: 'rgba(196,138,58,0.08)' }}>
          <Icon name="info" size={14} color={C.gold} style={{ flexShrink: 0 }} />
          <span style={{ ...sub, color: C.navy }}>Vergelijk ze naast elkaar op prijs, buurt en EPC.</span>
        </div>
      </div>
    </div>
  );
}

// ── Match (/match) ──────────────────────────────────────────────
function MatchGhost({ mode }) {
  const p = mode === 'couple' ? PROPS.demo_p2 : PROPS.demo_p1;
  return (
    <div style={{ ...screen, background: NAVY, minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, boxSizing: 'border-box' }}>
      <div style={{ textAlign: 'center', width: '100%' }} className="anim-pop">
        <div style={{ fontSize: 54, marginBottom: 8 }}>💛</div>
        <div style={{ color: '#fff', fontSize: 24, fontWeight: 900, letterSpacing: -0.3 }}>
          {mode === 'couple' ? 'Jullie hebben een koppelmatch!' : 'Je hebt een match!'}
        </div>
        <div style={{ color: GOLD, fontSize: 13, fontWeight: 800, marginTop: 6 }}>
          {mode === 'couple' ? 'Wondelgem — 94% overeenkomst voor jullie beiden' : 'Stationsstraat 14 — jouw beste match (92%)'}
        </div>
        <div style={{ ...card, marginTop: 18, textAlign: 'left', display: 'flex', gap: 12, alignItems: 'center', padding: 10 }}>
          <img src={p.img} alt={p.title} style={{ width: 84, height: 84, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
          <div>
            <div style={{ color: C.navy, fontSize: 13.5, fontWeight: 800 }}>{p.title}</div>
            <div style={{ ...sub, fontSize: 11, marginTop: 2 }}>{p.city} · {p.price} · {p.beds} slpk</div>
            <div style={{ display: 'flex', gap: 5, marginTop: 6, flexWrap: 'wrap' }}>
              {(mode === 'couple' ? ['Tuin', 'Station <10min', 'EPC B'] : ['Ligging', 'Tuin', 'Indeling']).map((r, i) => (
                <span key={i} style={{ background: 'rgba(196,138,58,0.14)', color: C.navy, fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 999 }}>{r}</span>
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <div style={{ flex: 1, background: GOLD, borderRadius: 999, padding: '13px 0', color: '#fff', fontSize: 12.5, fontWeight: 900 }}>📅 Bezoek plannen</div>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 999, padding: '13px 0', color: '#fff', fontSize: 12.5, fontWeight: 900 }}>✉️ Makelaar contacteren</div>
        </div>
      </div>
    </div>
  );
}

// ── Notificaties (/notificaties) ────────────────────────────────
function Notifs({ mode }) {
  return (
    <div style={screen}>
      <AppHeader mode={mode} />
      <div style={body}>
        <h2 style={h2}>Meldingen</h2>
        <div style={sub}>Relevant voor {mode === 'couple' ? 'jullie' : 'jouw'} zoektocht — geen ruis.</div>
        {NOTIFS[mode].map((n, i) => (
          <div key={i} className="anim-rise" style={{ ...card, display: 'flex', gap: 11, alignItems: 'flex-start', animationDelay: `${i * 0.18}s` }}>
            <div style={{ width: 34, height: 34, borderRadius: 17, background: `${n.accent}22`, border: `1px solid ${n.accent}66`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name={n.icon} size={14} color={n.accent} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ color: C.navy, fontSize: 12.5, fontWeight: 800 }}>{n.title}</span>
                <span style={{ color: C.inkFaint, fontSize: 10, flexShrink: 0 }}>{n.time}</span>
              </div>
              <div style={{ ...sub, fontSize: 11.5, marginTop: 2 }}>{n.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Financiering (/finance/hypotheek) ───────────────────────────
function Finance({ mode }) {
  const price = mode === 'couple' ? '€ 385.000' : '€ 305.000';
  const monthly = mode === 'couple' ? '€ 1.520' : '€ 1.240';
  return (
    <div style={screen}>
      <AppHeader mode={mode} />
      <div style={body}>
        <Eyebrow>FINANCIERING</Eyebrow>
        <h2 style={h2}>{mode === 'couple' ? 'Jullie hypotheeksimulatie' : 'Jouw hypotheeksimulatie'}</h2>
        <div style={sub}>IMMO MATCH berekent {mode === 'couple' ? 'jullie' : 'jouw'} haalbaarheid — {mode === 'couple' ? 'beide inkomens gecombineerd' : 'op basis van jouw situatie'}.</div>
        <div style={{ ...card, textAlign: 'center', padding: 22 }}>
          <div style={eyebrow}>GESCHATTE MAANDLAST</div>
          <div style={{ color: C.gold, fontSize: 40, fontWeight: 900, margin: '6px 0' }}>{monthly}<span style={{ fontSize: 14, color: C.inkMuted }}>/maand</span></div>
          <div style={{ ...sub, fontSize: 11.5 }}>Binnen {mode === 'couple' ? 'jullie' : 'jouw'} comfortzone</div>
        </div>
        <div style={card}>
          {[['Aankoopprijs', price], ['Eigen inbreng', '20%'], ['Looptijd', '20 jaar'], ['Rentevoet (indicatief)', '2,9%']].map(([l, v], i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: i ? '1px solid #F0EBE3' : 'none' }}>
              <span style={{ color: C.inkMuted, fontSize: 12.5 }}>{l}</span>
              <span style={{ color: C.navy, fontSize: 12.5, fontWeight: 800 }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={{ ...card, background: 'rgba(196,138,58,0.10)', borderColor: 'rgba(196,138,58,0.35)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Icon name="dollar-sign" size={14} color={C.gold} style={{ marginTop: 1, flexShrink: 0 }} />
          <span style={{ ...sub, color: C.navy }}>
            {mode === 'couple' ? 'Volgende stap: gezamenlijk hypotheek-consult?' : 'Met €305.000 en 20% eigen inbreng blijf je ruim binnen je comfortzone.'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Wooncoach (/wooncoach) ──────────────────────────────────────
function WooncoachGhost({ mode }) {
  return (
    <div style={screen}>
      <AppHeader mode={mode} />
      <div style={body}>
        <div style={{ ...card, padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 18, background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="compass" size={17} color="#fff" />
            </div>
            <div style={{ color: C.navy, fontSize: 15, fontWeight: 900 }}>Goedemorgen ☀️</div>
          </div>
          <div style={{ ...sub, marginTop: 10, fontSize: 12.5, lineHeight: '18px' }}>
            {mode === 'couple'
              ? 'Jullie koppelmatch op Wondelgem is bevestigd voor donderdag 10:00. Ik heb een voorbereidingschecklist klaargezet voor jullie beiden.'
              : 'Je bezoek aan Stationsstraat 14 is bevestigd voor zaterdag 14:00. Ik heb 5 checkvragen klaar om het bezoek voor te bereiden.'}
          </div>
        </div>
        <div style={{ ...card, background: 'rgba(196,138,58,0.10)', borderColor: 'rgba(196,138,58,0.35)' }}>
          <div style={{ color: C.gold, fontSize: 10, fontWeight: 900, letterSpacing: 1.2 }}>MIJN ADVIES VANDAAG</div>
          <div style={{ color: C.navy, fontSize: 12.5, marginTop: 5, lineHeight: '17px' }}>
            {mode === 'couple'
              ? 'Bereid samen jullie bod-strategie voor. Ik verzamel de argumenten: 94% overlap, sterke ligging en EPC B.'
              : 'Bod-strategie: de prijs zakte met €15.000 en het aantal bewaarders stijgt. Wacht niet te lang met een openingsbod.'}
          </div>
        </div>
        {[['flag', 'Volgende stap', mode === 'couple' ? 'Gezamenlijk bod voorbereiden' : 'Bezoek zaterdag 14:00'], ['trending-down', 'Signaal', mode === 'couple' ? 'Aanbod karakterhuizen +6% in Gent' : 'Prijsdaling —€15.000 op je match'], ['calendar', 'Agenda', mode === 'couple' ? 'Bezoek Do 10:00 — bevestigd' : 'Bezoek Za 14:00 — bevestigd']].map(([ic, l, v], i) => (
          <div key={i} style={{ ...card, display: 'flex', gap: 11, alignItems: 'center' }}>
            <Icon name={ic} size={15} color={C.gold} />
            <div>
              <div style={{ color: C.inkMuted, fontSize: 10, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase' }}>{l}</div>
              <div style={{ color: C.navy, fontSize: 12.5, fontWeight: 700, marginTop: 1 }}>{v}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Checklist / woonreis (/checklist) ───────────────────────────
function Checklist({ mode }) {
  const current = mode === 'couple' ? 'Beslissen' : 'Bezoek inplannen';
  const phases = ['Oriënteren', 'Vormgeven', 'Dagelijkse puls', 'Dichterbij', 'Bezoek inplannen', 'Beslissen', 'Verhuizen'];
  const curIdx = phases.indexOf(current);
  return (
    <div style={screen}>
      <AppHeader mode={mode} />
      <div style={body}>
        <Eyebrow>WOONREIS</Eyebrow>
        <h2 style={h2}>{mode === 'couple' ? 'Jullie woonreis' : 'Jouw woonreis'}</h2>
        <div style={sub}>Fase: {current} · IMMO MATCH beweegt met {mode === 'couple' ? 'jullie' : 'je'} mee.</div>
        <div style={card}>
          {phases.map((p, i) => {
            const done = i < curIdx;
            const isCur = i === curIdx;
            return (
              <div key={i} className="anim-rise" style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 0', animationDelay: `${i * 0.12}s` }}>
                <div style={{
                  width: 26, height: 26, borderRadius: 13, flexShrink: 0,
                  background: done ? C.green : isCur ? C.gold : '#F0EBE3',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {done ? <Icon name="check" size={12} color="#fff" /> : isCur ? <Icon name="flag" size={11} color="#fff" /> : null}
                </div>
                <span style={{ color: isCur ? C.navy : done ? C.inkMuted : C.inkFaint, fontSize: 13, fontWeight: isCur ? 900 : 600 }}>{p}</span>
                {isCur ? <span style={{ marginLeft: 'auto', background: 'rgba(196,138,58,0.14)', color: C.gold, fontSize: 9, fontWeight: 900, padding: '3px 8px', borderRadius: 999 }}>NU</span> : null}
              </div>
            );
          })}
        </div>
        <div style={{ ...card, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Icon name="compass" size={14} color={C.gold} style={{ marginTop: 1, flexShrink: 0 }} />
          <span style={{ ...sub, color: C.navy }}>
            {mode === 'couple' ? 'Jullie zijn nu in fase Beslissen. Volgende stap: gezamenlijk bod voorbereiden.' : 'Jouw volgende stap is een bezoek. Ik hou de rest bij: financiering, notaris, verhuizen.'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Nieuws / MarktPulse (/nieuws) ───────────────────────────────
function Nieuws({ mode }) {
  return (
    <div style={screen}>
      <AppHeader mode={mode} />
      <div style={body}>
        <Eyebrow>MARKTPULSE</Eyebrow>
        <h2 style={h2}>Markt & nieuws — Gent</h2>
        <div style={sub}>Prijzen · aanbod · trends op {mode === 'couple' ? 'jullie' : 'jouw'} budget. Geen ruis.</div>
        {MARKTPULSE.map((m, i) => (
          <div key={i} className="anim-rise" style={{ ...card, animationDelay: `${i * 0.15}s` }}>
            <div style={{ ...eyebrow, marginBottom: 5 }}>{m.tag}</div>
            <div style={{ color: C.navy, fontSize: 14, fontWeight: 900 }}>{m.title}</div>
            <div style={{ ...sub, marginTop: 3 }}>{m.body}</div>
          </div>
        ))}
        <div style={{ ...card, background: C.navy, border: 'none' }}>
          <div style={{ color: '#fff', fontSize: 13, fontWeight: 800 }}>Wat betekent dit voor {mode === 'couple' ? 'jullie' : 'jou'}?</div>
          <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11.5, marginTop: 4, lineHeight: '16px' }}>
            Gent-centrum: gemiddelde prijs €3.240/m² · aanbod stabiel. {mode === 'couple' ? 'Jullie' : 'Jouw'} budget-zone is goed vertegenwoordigd.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Agenda-overlap (/demo/agenda-overlap) — port van agenda-overlap.tsx ──
const TOM_SLOTS = [{ day: 'Ma', time: '18-20u' }, { day: 'Wo', time: '19-21u' }, { day: 'Do', time: '10-12u' }, { day: 'Za', time: '14-16u' }];
const EMMA_SLOTS = [{ day: 'Di', time: '17-19u' }, { day: 'Do', time: '10-12u' }, { day: 'Vr', time: '18-20u' }, { day: 'Za', time: '14-16u' }];
const OVERLAP = [{ day: 'Do', time: '10-12u' }, { day: 'Za', time: '14-16u' }];
const isOverlap = (s) => OVERLAP.some((o) => o.day === s.day && o.time === s.time);

function SlotChip({ s, person, delay }) {
  const ov = isOverlap(s);
  return (
    <div className="anim-pop" style={{
      animationDelay: `${delay}ms`,
      padding: '7px 10px', borderRadius: 10, minWidth: 60, textAlign: 'center',
      background: person === 'tom' ? 'rgba(16,42,67,0.10)' : 'rgba(196,138,58,0.14)',
      border: `${ov ? 2 : 1}px solid ${person === 'tom' ? C.navy : C.gold}`,
      boxShadow: ov ? '0 0 8px rgba(34,197,94,0.35)' : 'none',
    }}>
      <div style={{ color: C.navy, fontSize: 10, fontWeight: 800, letterSpacing: 0.5 }}>{s.day}</div>
      <div style={{ color: C.navy, fontSize: 11, fontWeight: 700, marginTop: 1 }}>{s.time}</div>
    </div>
  );
}

function AgendaOverlap() {
  return (
    <div style={screen} data-testid="demo-agenda-overlap">
      <div style={{ ...body, gap: 14, paddingTop: 52 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Eyebrow>📅 GEDEELDE AGENDA</Eyebrow>
          <h2 style={{ ...h2, fontSize: 22 }}>Wanneer kunnen jullie samen?</h2>
          <div style={sub}>IMMO MATCH vergelijkt Toms en Emma's agenda's en vindt automatisch een gezamenlijk moment.</div>
        </div>

        {/* Tom */}
        <div className="anim-fade-in" style={{ ...card, animationDelay: '0ms' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <Avatar letter="T" bg={C.navy} />
            <div style={{ flex: 1 }}>
              <div style={{ color: C.navy, fontSize: 15, fontWeight: 800 }}>Tom</div>
              <div style={{ color: C.inkMuted, fontSize: 11 }}>Deelt zijn beschikbaarheid</div>
            </div>
            <Icon name="check-circle" size={16} color={C.navy} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {TOM_SLOTS.map((s, i) => <SlotChip key={i} s={s} person="tom" delay={500 + i * 300} />)}
          </div>
        </div>

        {/* Emma */}
        <div className="anim-fade-in" style={{ ...card, animationDelay: '2200ms' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <Avatar letter="E" bg={C.gold} dark />
            <div style={{ flex: 1 }}>
              <div style={{ color: C.navy, fontSize: 15, fontWeight: 800 }}>Emma</div>
              <div style={{ color: C.inkMuted, fontSize: 11 }}>Deelt haar beschikbaarheid</div>
            </div>
            <Icon name="check-circle" size={16} color={C.gold} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {EMMA_SLOTS.map((s, i) => <SlotChip key={i} s={s} person="emma" delay={2500 + i * 300} />)}
          </div>
        </div>

        {/* Overlap-card */}
        <div className="anim-rise" style={{ ...card, border: `2px solid ${C.green}`, animationDelay: '5200ms' }}>
          <div className="anim-pop" style={{ animationDelay: '5400ms', alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, background: C.green, padding: '5px 10px', borderRadius: 999 }}>
            <span style={{ fontSize: 14 }}>🎉</span>
            <span style={{ color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: 1.2 }}>OVERLAP GEVONDEN</span>
          </div>
          <div style={{ color: C.navy, fontSize: 16, fontWeight: 800, marginTop: 8 }}>Jullie kunnen allebei</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            {OVERLAP.map((s, i) => (
              <div key={i} style={{ padding: '7px 10px', borderRadius: 10, minWidth: 60, textAlign: 'center', background: C.green }}>
                <div style={{ color: '#fff', fontSize: 10, fontWeight: 800 }}>{s.day}</div>
                <div style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>{s.time}</div>
              </div>
            ))}
          </div>
          <div style={{ color: C.inkMuted, fontSize: 11.5, marginTop: 8 }}>IMMO MATCH stelt automatisch het beste moment voor.</div>
          <div className="anim-pop" style={{ animationDelay: '6600ms', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: C.navy, padding: '12px 0', borderRadius: 999, marginTop: 10 }}>
            <Icon name="calendar" size={15} color="#fff" />
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 800 }}>Bezoek bevestigen · Donderdag 10:00</span>
          </div>
        </div>

        {/* Bevestigd */}
        <div className="anim-pop" style={{ animationDelay: '8000ms', display: 'flex', alignItems: 'center', gap: 12, background: C.green, padding: 14, borderRadius: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 20, background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="check" size={22} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#fff', fontSize: 15, fontWeight: 800 }}>Bezoek bevestigd</div>
            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 11.5, marginTop: 2, lineHeight: '15px' }}>Do 10:00 · zichtbaar in beide agenda's · agenda-invite verstuurd</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Deeplink → scherm mapping ───────────────────────────────────
// (tabs)-schermen zijn 1-op-1 ports van de echte app-code (app/(tabs)/*.tsx).
export function screenFor(step, { mode }) {
  const dl = (step?.deeplink || '').split('?')[0];
  if (dl === '/demo/splash') return { node: <Splash /> };
  if (dl === '/onboarding/intro') return { node: <OnbIntro mode={mode} /> };
  if (dl === '/onboarding/mode') return { node: <OnbMode mode={mode} /> };
  if (dl === '/onboarding/partner') return { node: <OnbPartner /> };
  if (dl === '/onboarding/profile') return { node: <OnbProfile mode={mode} /> };
  if (dl === '/onboarding/type') return { node: <OnbType mode={mode} /> };
  if (dl === '/onboarding/preferences') return { node: <OnbPrefs mode={mode} step={step} /> };
  if (dl === '/onboarding/searching') return { node: <OnbSearching mode={mode} /> };
  if (dl === '/onboarding/first-match') return { node: <OnbFirstMatch mode={mode} step={step} /> };
  if (dl === '/(tabs)/ontdek') return { node: <OntdekApp mode={mode} />, tab: 'ontdek' };
  if (dl === '/(tabs)/swipe') return { node: <SwipeApp mode={mode} />, tab: 'swipe' };
  if (dl.startsWith('/property/')) return { node: <PropertyDetail mode={mode} /> };
  if (dl === '/maybes') return { node: <Maybes mode={mode} /> };
  if (dl === '/match') return { node: <MatchGhost mode={mode} /> };
  if (dl === '/notificaties') return { node: <Notifs mode={mode} /> };
  if (dl === '/(tabs)/bezoeken') return { node: <BezoekenApp mode={mode} step={step} />, tab: 'bezoeken' };
  if (dl === '/finance/hypotheek') return { node: <Finance mode={mode} /> };
  if (dl === '/wooncoach') return { node: <WooncoachGhost mode={mode} /> };
  if (dl === '/checklist') return { node: <Checklist mode={mode} /> };
  if (dl === '/nieuws') return { node: <Nieuws mode={mode} /> };
  if (dl === '/demo/agenda-overlap') return { node: <AgendaOverlap /> };
  return { node: <OntdekApp mode={mode} />, tab: 'ontdek' };
}
